import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchUserDetails, updateUserProfile } from "../../../redux/userSlice";
import { getContainerClass } from "../../../components/navigation/navigation";
import "./editprofile.css";
import { CircularProgress, Skeleton } from '@mui/material';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import Header from "../../../components/header/header";
import Cover from "../../../components/cover/cover";

const EditProfile = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const location = useLocation();
  // const userState = useSelector((state) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    companyName: "",
    phone: "",
    email: "",
    profilePic: ""
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = React.useRef(null);

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    const requiredFields = ['firstName', 'lastName', 'gender', 'companyName', 'phone'];
    return requiredFields.every(field => formData[field] && formData[field].trim() !== '');
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await dispatch(fetchUserDetails()).unwrap();
        if (result?.userDetail) {
          setFormData({
            firstName: result.userDetail.firstName || "",
            lastName: result.userDetail.lastName || "",
            gender: result.userDetail.gender || "",
            companyName: result.userDetail.companyName || "",
            phone: result.userDetail.phone || "",
            email: result.userDetail.email || "",
            profilePic: result.userDetail.profilePic || ""
          });
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        toast.error("Failed to load profile data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      // Check file type
      if (!file.type.match('image.*')) {
        toast.error("Please select an image file");
        return;
      }

      setImageLoading(true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          profilePic: reader.result
        }));
        setImageLoading(false);
        toast.success("Image uploaded successfully.");
      };
      reader.onerror = () => {
        console.error("Error reading file");
        toast.error("Error processing image. Please try again.");
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const validatePhoneNumber = (phone) => {
    // Remove any spaces or hyphens
    const cleanedPhone = phone.replace(/[\s-]/g, '');
    
    // Pakistani number: +92 followed by 10 digits
    const pakRegex = /^\+923[0-4]\d{8}$/;
    // UAE/Dubai number: +971 followed by 9 digits
    const uaeRegex = /^\+9715[0-9]\d{7}$/;
    
    if (!pakRegex.test(cleanedPhone) && !uaeRegex.test(cleanedPhone)) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    // Validate phone number
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      setError("Please enter a valid phone number");
      toast.error("Invalid phone number format");
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await dispatch(updateUserProfile(formData)).unwrap();
      if (result.success) {
        setSuccess(result.message || "Profile has been successfully updated");
        toast.success(result.message || "Profile has been successfully updated");
      } else {
        setError(result.message || "Failed to update profile");
        toast.error(result.message || "Failed to update profile");
      }
    } catch (error) {
      console.log(error);
      setError("An error occurred while updating the profile");
      toast.error("An error occurred while updating the profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProfileImage = () => {
    if (imageLoading) {
      return (
        <div className="image-loading">
          <CircularProgress size={50} />
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="profile-pic-wrapper">
          <Skeleton
            variant="circular"
            width={150}
            height={150}
            animation="wave"
            className="profile-image-skeleton"
          />
        </div>
      );
    }

    return (
      <div className="profile-pic-wrapper" onClick={handleImageClick} style={{ cursor: 'pointer' }}>
        <img
          src={formData.profilePic || "https://media.istockphoto.com/id/1682296067/photo/happy-studio-portrait-or-professional-man-real-estate-agent-or-asian-businessman-smile-for.jpg?s=612x612&w=0&k=20&c=9zbG2-9fl741fbTWw5fNgcEEe4ll-JegrGlQQ6m54rg="}
          alt="Profile"
          title="Click to change profile photo"
          className="profile-image"
        />
        <div className="profile-pic-overlay">Change Photo</div>
      </div>
    );
  };

  const renderInputField = (name, placeholder, type = "text", disabled = false) => {
    if (isLoading) {
      return (
        <Skeleton
          variant="rectangular"
          height={40}
          animation="wave"
          className="input-skeleton"
        />
      );
    }

    if (name === "gender") {
      return (
        <div className="input-container">
          <label className="input-label" style={{marginBottom: "2px"}}>{placeholder}</label>
          <select
            name={name}
            value={formData[name]}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      );
    }

    return (
      <div className="input-container">
        <label className="input-label" style={{marginBottom: "2px"}}>{placeholder}</label>
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    );
  };

  const breadcrumb = ["Home", "Edit Profile"];

  return (
    <>
      <div className={getContainerClass(location.pathname)}>
        {/* <Header /> */}
        <Cover breadcrumb={breadcrumb} page="editprofile" />
        <div className="edit-profile-content">
          <h1>Edit Profile</h1>
          <div className="profile-image-container">
            {renderProfileImage()}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              disabled={imageLoading}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message-profile">{success}</div>}
          <div className="edit-profile-form">
            {renderInputField("firstName", "First name*")}
            {renderInputField("lastName", "Last name*")}
            {renderInputField("gender", "Select Gender*")}
            {renderInputField("companyName", "Company name*")}
            {renderInputField("phone", "Phone number*", "tel")}
            {renderInputField("email", "Email address*", "email", true)}
            <button 
              onClick={handleSubmit} 
              disabled={isSubmitting || isLoading || !isFormValid()}
              className={!isFormValid() ? 'disabled-button' : ''}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProfile;